import type { ReactNode } from 'react'
import { useEffect, useId, useRef } from 'react'

import { Backdrop } from './Backdrop'
import { IconButton } from './Button'

import { cn } from '@/lib/utils/cn'


/**
 * A modal dialog.
 *
 * Focus is trapped on open and restored to the trigger on close. Escape closes
 * unless the caller blocks it (a dirty form). Body scroll locks WITHOUT layout
 * shift, because `scrollbar-gutter: stable` is set globally in base.css.
 */
export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  closeOnEscape = true,
}: {
  open: boolean
  onClose: () => void
  title: string
  description?: string | undefined
  children: ReactNode
  footer?: ReactNode | undefined
  size?: 'sm' | 'md' | 'lg'
  closeOnEscape?: boolean
}) {
  const titleId = useId()
  const descriptionId = useId()
  const panelRef = useRef<HTMLDivElement>(null)
  const previouslyFocused = useRef<HTMLElement | null>(null)

  // Remember what had focus, move focus in, and put it back on close.
  useEffect(() => {
    if (!open) return
    previouslyFocused.current = document.activeElement as HTMLElement | null

    const firstFocusable = panelRef.current?.querySelector<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    )
    firstFocusable?.focus()

    return () => previouslyFocused.current?.focus()
  }, [open])

  // Lock body scroll while open.
  useEffect(() => {
    if (!open) return
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previous
    }
  }, [open])

  // Escape and Tab-trapping are handled at the DOCUMENT level, not on JSX.
  // A modal must respond wherever focus is inside it, and attaching keyboard
  // handlers to a non-interactive wrapper is both an a11y smell and fragile.
  useEffect(() => {
    if (!open) return

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape' && closeOnEscape) {
        event.stopPropagation()
        onClose()
        return
      }
      if (event.key !== 'Tab') return

      const focusable = panelRef.current?.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), ' +
          'select:not([disabled]), textarea:not([disabled]), ' +
          '[tabindex]:not([tabindex="-1"])',
      )
      if (!focusable?.length) return

      const first = focusable[0]!
      const last = focusable[focusable.length - 1]!

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown, true)
    return () => document.removeEventListener('keydown', onKeyDown, true)
  }, [open, closeOnEscape, onClose])

  if (!open) return null

  const widths = { sm: 'max-w-sm', md: 'max-w-lg', lg: 'max-w-3xl' } as const

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4">
      <Backdrop onClick={onClose} label="Close dialog" />

      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        className={cn(
          'panel relative z-50 w-full rounded-card bg-surface shadow-overlay',
          'max-h-[calc(100dvh-2rem)] overflow-auto',
          widths[size],
        )}
        data-state="open"
      >
        <header className="flex items-start justify-between gap-4 p-5 pb-3">
          <div className="min-w-0">
            <h2 id={titleId} className="text-subtitle font-semibold">
              {title}
            </h2>
            {description && (
              <p id={descriptionId} className="mt-1 text-compact text-text-secondary">
                {description}
              </p>
            )}
          </div>
          <IconButton
            variant="ghost"
            size="sm"
            label="Close"
            icon="✕"
            onClick={onClose}
          />
        </header>

        <div className="px-5 pb-5">{children}</div>

        {footer && (
          <footer className="flex justify-end gap-3 border-t border-border p-5">
            {footer}
          </footer>
        )}
      </div>
    </div>
  )
}
